// The proposed answer's canonical format is constrained Markdown: flat "- "
// and "1." lists, **bold**, *italic*, single newlines as hard line breaks
// (mirroring remark-breaks in the display path), blank lines as paragraph
// separators. HTML exists only at the boundaries — outgoing email bodies and
// transient editor state — and this module is the only markdown→HTML crossing
// point. The backend enforces the subset on generation
// (jupiter/utils/text.py enforce_answer_markdown_subset), so anything outside
// it here is operator-typed and simply renders as literal text.

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

// CommonMark parity: emphasis content may not start or end with whitespace
// ("* b *" stays literal), which is also what react-markdown renders.
const BOLD_RE = /\*\*(\S(?:[^*]*?\S)?)\*\*/g;
const ITALIC_RE = /\*(\S(?:[^*\n]*?\S)?)\*/g;

const renderInline = (s: string): string =>
  escapeHtml(s)
    .replace(BOLD_RE, "<strong>$1</strong>")
    .replace(ITALIC_RE, "<em>$1</em>");

const BULLET_RE = /^[-*+]\s+/;
const NUMBERED_RE = /^(\d+)[.)]\s+/;

type Run =
  | { kind: "text"; lines: string[] }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[]; start: number };

// Groups a block's lines into consecutive runs of plain text, bullet items,
// and numbered items. Matches how react-markdown segments the same input: a
// list may directly follow a text line without a blank line in between.
const groupLines = (lines: string[]): Run[] => {
  const runs: Run[] = [];
  for (const line of lines) {
    const prev = runs[runs.length - 1];
    const numbered = NUMBERED_RE.exec(line);
    if (numbered) {
      const item = line.replace(NUMBERED_RE, "");
      if (prev?.kind === "ol") prev.items.push(item);
      else runs.push({ kind: "ol", items: [item], start: parseInt(numbered[1], 10) });
    } else if (BULLET_RE.test(line)) {
      const item = line.replace(BULLET_RE, "");
      if (prev?.kind === "ul") prev.items.push(item);
      else runs.push({ kind: "ul", items: [item] });
    } else if (prev?.kind === "text") {
      prev.lines.push(line);
    } else {
      runs.push({ kind: "text", lines: [line] });
    }
  }
  return runs;
};

const renderRun = (run: Run): string => {
  switch (run.kind) {
    case "text":
      return `<p>${run.lines.map(renderInline).join("<br>")}</p>`;
    case "ul":
      return `<ul>${run.items.map((i) => `<li>${renderInline(i)}</li>`).join("")}</ul>`;
    case "ol": {
      const start = run.start !== 1 ? ` start="${run.start}"` : "";
      return `<ol${start}>${run.items.map((i) => `<li>${renderInline(i)}</li>`).join("")}</ol>`;
    }
  }
};

/**
 * Convert a constrained-markdown answer into email-body HTML.
 *
 * All text is HTML-escaped before markup is added, so a "<" or "&" in an
 * answer can never become live markup in the outgoing mail. Single newlines
 * become <br> (display parity with remark-breaks), blank lines separate
 * paragraphs. Strip citation markers before calling this.
 */
export function markdownToEmailHtml(markdown: string): string {
  const blocks = markdown
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  if (blocks.length === 0) return "<p></p>";
  return blocks
    .map((block) => groupLines(block.split("\n").map((l) => l.trim())).map(renderRun).join(""))
    .join("");
}

// Classic Outlook (Word engine) writes one <p class="MsoNormal"> per Enter with
// margin:0 and a blank line as an empty paragraph, never <br>. This is the
// outgoing shape for tenants that opt in (feature.outlook-native-html), so a
// pasted or inserted answer looks like mail typed in Outlook. The editor keeps
// markdownToEmailHtml: editorHtml.ts is its inverse and needs the <br> shape.
const OUTLOOK_P_OPEN = '<p style="margin:0">';
const OUTLOOK_BLANK_LINE = `${OUTLOOK_P_OPEN}&nbsp;</p>`;
const OUTLOOK_LIST_STYLE = ' style="margin-top:0;margin-bottom:0"';

const renderOutlookRun = (run: Run): string => {
  switch (run.kind) {
    case "text":
      return run.lines.map((l) => `${OUTLOOK_P_OPEN}${renderInline(l)}</p>`).join("");
    case "ul":
      return `<ul${OUTLOOK_LIST_STYLE}>${run.items.map((i) => `<li>${renderInline(i)}</li>`).join("")}</ul>`;
    case "ol": {
      const start = run.start !== 1 ? ` start="${run.start}"` : "";
      return `<ol${OUTLOOK_LIST_STYLE}${start}>${run.items.map((i) => `<li>${renderInline(i)}</li>`).join("")}</ol>`;
    }
  }
};

/**
 * Convert a constrained-markdown answer into the HTML classic Outlook itself
 * produces: one zero-margin paragraph per line, an empty paragraph where the
 * answer has a blank line. Fonts are left out so Outlook applies the user's
 * default. Escaping is identical to markdownToEmailHtml.
 */
export function markdownToOutlookHtml(markdown: string): string {
  const blocks = markdown
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  if (blocks.length === 0) return "<p></p>";
  return blocks
    .map((block) => groupLines(block.split("\n").map((l) => l.trim())).map(renderOutlookRun).join(""))
    .join(OUTLOOK_BLANK_LINE);
}

/**
 * Strip emphasis markers for plain-text destinations (clipboard, plain-text
 * paste targets). List prefixes are kept — "- " and "1." read naturally as
 * plain text, bare asterisks do not.
 */
export function stripEmphasisMarkers(markdown: string): string {
  return markdown.replace(BOLD_RE, "$1").replace(ITALIC_RE, "$1");
}

/**
 * Render a plain-text signature as email HTML: escaped, blank lines separate
 * paragraphs, single newlines become <br>. Deliberately no markdown — a "*" in
 * a signature ("Tel. *123") is literal text.
 */
export function plainTextToEmailHtml(text: string): string {
  return text
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean)
    .map((block) => `<p>${block.split("\n").map((l) => escapeHtml(l.trim())).join("<br>")}</p>`)
    .join("");
}

/**
 * The HTML signature to send: the stored HTML variant when there is one, else
 * the plain-text signature rendered as HTML. That fallback is what keeps a
 * signature written before the HTML variant existed working on direct send.
 * Null when the user has neither.
 */
export function signatureToEmailHtml(sig: {
  emailSignature?: string | null;
  emailSignatureHtml?: string | null;
}): string | null {
  const html = sig.emailSignatureHtml?.trim();
  if (html) return html;
  const text = sig.emailSignature?.trim();
  return text ? plainTextToEmailHtml(text) : null;
}

const BLOCK_TAGS = new Set(["P", "DIV", "UL", "OL", "LI", "H1", "H2", "H3", "H4", "H5", "H6", "TABLE", "TR"]);

/**
 * Derive the plain-text variant of an HTML signature. The HTML editor is the
 * only thing a user writes on a direct-send setup; the text variant (clipboard
 * text flavor, the Outlook hand-off) is computed from it on save so the two
 * can never drift. Block ends become line breaks, a link whose text differs
 * from its target keeps the target in parentheses, and everything else is text.
 */
export function htmlToPlainText(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  let out = "";
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += (node.textContent ?? "").replace(/\s+/g, " ");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as Element;
    if (el.tagName === "BR") {
      out += "\n";
      return;
    }
    if (el.tagName === "LI") out += "- ";
    el.childNodes.forEach(walk);
    if (el.tagName === "A") {
      const href = el.getAttribute("href")?.trim();
      const text = el.textContent?.trim() ?? "";
      if (href && href !== text && href !== `mailto:${text}`) out += ` (${href.replace(/^mailto:/, "")})`;
    }
    if (el.tagName === "LI") out += "\n";
    else if (BLOCK_TAGS.has(el.tagName)) out += "\n\n";
  };
  doc.body.childNodes.forEach(walk);
  return out
    .split("\n")
    .map((l) => l.trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
